import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { Store } from '@ngrx/store'
import { catchError, map, of, Subscription } from 'rxjs'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import {
  ActivityAPIModel,
  CommentAPIModel,
  LocationAPIModel,
  StatusAPIModel,
  TaskTypeAPIModel,
  UserAPIModel
} from 'src/app/models/api.model'
import { ActivityService } from 'src/app/services/activity/activity.service'
import { CommonService, formatDateToDDMMYYYY, formatDatetoYYYYMMDD } from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { Form11Service } from 'src/app/services/form11/form11.service'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { getUserFromState } from 'src/app/store/selector/initialize.selector'
import { AppState } from 'src/app/store/state/app.state'
import { addDisabledFormControl, addFormControl } from 'src/app/utils/form-control.util'
import { minOptionalLengthValidator } from 'src/app/validators/validators.validator'
import cloneDeep from 'lodash/cloneDeep'
import Swal from 'sweetalert2'

@Component({
  selector: 'app-activity-details',
  templateUrl: './activity-details.component.html',
  styleUrls: ['./activity-details.component.scss']
})
export class ActivityDetailsComponent implements OnInit, OnDestroy {
  readonly ACTIVITY_SUMMARY_LINK = `/${GlobalConstants.ROUTING.ACTIVITY}/${GlobalConstants.ROUTING.SUMMARY}`
  readonly userType = GlobalConstants.USER_TYPE
  readonly ASSIGNED_TO_CONTROL_NAME = 'assignedTo'
  readonly RESPONSE_DUE_DATE_TIME_CONTROL_NAME = 'responseDueDatetime'
  private readonly CREATED_DATE_TIME_CONTROL_NAME = 'createdDatetime'
  readonly MODULENAME = GlobalConstants.MODULE.ACTIVITY_TRACKER
  private readonly STATUS_CONTROL_NAME = 'status'
  readonly OWNER_DISPLAY_ID_CONTROL_NAME = 'ownerDisplayId'
  readonly EMP_NAME_CONTROL_NAME = 'empName'
  readonly EMP_STATUS_CONTROL_NAME = 'empStatus'
  readonly EMP_CURRENT_PF_NO_CONTROL_NAME = 'empCurrentPfNumber'
  readonly EMP_UAN_NO_CONTROL_NAME = 'empUanNumber'
  readonly LOCATION_CONTROL_NAME = 'location'

  activityDetailsForm: FormGroup
  activity: ActivityAPIModel
  commentsToSave: CommentAPIModel[] = []
  private id: string
  user$: Subscription
  user: UserAPIModel
  filesToBeUploaded: File[]
  statusValueChange: boolean = false
  isDisabled: boolean = false
  hasFormChanged: boolean
  notify: boolean
  selectTaskTypesSubscription$: Subscription
  isEmployeeLinkedTask: boolean | undefined
  isHeadcountRequired: boolean | undefined
  locationOptions: Map<string, string>

  switchValue: boolean = true

  constructor(
    private activityService: ActivityService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private cs: CommonService,
    private documentService: DocumentService,
    private appState: Store<AppState>,
    private initializeService: InitializeService,
    private form11Service: Form11Service,
    private cd: ChangeDetectorRef
  ) {}

  addAssignedToForm = addFormControl(this.ASSIGNED_TO_CONTROL_NAME, '')
  addOwnerDisplayIdToForm = addDisabledFormControl(this.OWNER_DISPLAY_ID_CONTROL_NAME, '')
  addEmpNameToForm = addDisabledFormControl(this.EMP_NAME_CONTROL_NAME, '')
  addEmpStatusToForm = addDisabledFormControl(this.EMP_STATUS_CONTROL_NAME, '')
  addEmpCurrentPfNumberToForm = addDisabledFormControl(this.EMP_CURRENT_PF_NO_CONTROL_NAME, '')
  addEmpUanNumberToForm = addDisabledFormControl(this.EMP_UAN_NO_CONTROL_NAME, '')

  ngOnInit() {
    this.getUserDetailsFromStore()
    this.buildActivityDetailsForm()
    this.route.params.subscribe(params => {
      const id = params['id']
      this.id = id
      this.dispatchFetchActivityDetailsAction(id)
      this.getActivityDetailsFromStore()
    })
  }

  getUserDetailsFromStore = () => {
    this.user$ = this.appState.select(getUserFromState).subscribe((user: UserAPIModel) => {
      if (user) {
        this.user = user
        this.updateFields()
      }
    })
  }

  private buildActivityDetailsForm() {
    this.activityDetailsForm = this.formBuilder.group({
      displayId: [{ value: '', disabled: true }],
      name: [{ value: '', disabled: true }],
      activityType: [{ value: '', disabled: true }],
      [this.CREATED_DATE_TIME_CONTROL_NAME]: [{ value: '', disabled: true }],
      [this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]: [{ value: '' }],
      status: ['', [Validators.required]],
      detailedStatus: ['', [minOptionalLengthValidator(6)]],
      description: [{ value: '', disabled: true }, []],
      statusUpdateFlag: [false, []],
      daysElapsed: [{ value: '', disabled: true }, []],
      notify: [false],
      location: [''],
      headcount: ['', [Validators.maxLength(5), Validators.pattern(/^[0-9]*$/)]]
    })
  }

  get f() {
    return this.activityDetailsForm?.controls
  }

  private dispatchFetchActivityDetailsAction(id: any) {
    this.activityService.dispatchFetchActivityDetailsAction(id)
  }

  private getActivityDetailsFromStore() {
    this.activityService.getActivityDetailsFromStore().subscribe(activity => {
      if (activity) {
        this.activity = activity
        this.activityDetailsForm.patchValue({
          ...activity,
          [this.CREATED_DATE_TIME_CONTROL_NAME]: formatDateToDDMMYYYY(activity.createdDatetime)
        })
        this.updateFields()
        this.setSelectedActivityType(this.activity)
      }
    })
  }

  private updateFields() {
    const activity = this.activity
    if (activity && this.user?.userType) {
      const responseDueDatetime =
        this.user.userType == GlobalConstants.USER_TYPE.EMPLOYEE_ADMIN
          ? formatDateToDDMMYYYY(activity.responseDueDatetime)
          : formatDatetoYYYYMMDD(activity.responseDueDatetime)
      this.activityDetailsForm.patchValue({
        [this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]: responseDueDatetime
      })
      this.addAssignedToForm(this.activityDetailsForm)
      this.f?.[this.ASSIGNED_TO_CONTROL_NAME].setValue(this.activity.assignedTo)
      if (this.user.userType === this.userType.EMPLOYEE_ADMIN) {
        this.activityDetailsForm?.get(this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME)?.disable()
      }
      this.loadLocationsForCompany(activity?.companyEntityId)
      this.subscribeFormForValueChanges(this.activityDetailsForm)
    }
  }

  addComment(commentAPIModel: CommentAPIModel) {
    this.commentsToSave = [commentAPIModel]
  }

  getIfNotifySelected(notify) {
    this.notify = notify || false
  }

  updateActivity() {
    if (this.activityDetailsForm.invalid) {
      return this.cs.showError('Invalid Input')
    }

    const commentToBeSaved = this.commentsToSave && this.commentsToSave[0]?.comment ? this.commentsToSave : []
    const notifyState = this.determineNotifyState(commentToBeSaved) || false

    this.activityService.dispatchUpdateActivityAction(
      {
        ...this.activityDetailsForm.value,
        companyEntityId: this.activity.companyEntityId,
        statusUpdateFlag: this.statusValueChange,
        comments: commentToBeSaved,
        name: this.activity.name,
        notify: this.statusValueChange ? false : notifyState
      },
      this.id,
      this.filesToBeUploaded,
      this.ACTIVITY_SUMMARY_LINK
    )
  }

  downloadActivity() {
    const endPoint = ServiceUrlConstants.createDownloadExportPDFEndpoint('ACTIVITY', this.id)
    this.documentService.downloadDocument(endPoint)
  }

  onStatusSelected(status: StatusAPIModel) {
    this.activityDetailsForm.controls[this.STATUS_CONTROL_NAME].setValue(status.code)
  }

  handleAssignToSelectedValue(assignedTo: string) {
    this.activityDetailsForm.patchValue({ assignedTo })
  }

  handleFileInput(files: File[]) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  onStatusChange(flag: boolean) {
    this.statusValueChange = flag
    this.isDisabled = flag
  }

  mandatoryflag(flag: boolean) {
    if (this.statusValueChange && flag) {
      this.isDisabled = true
    } else {
      this.isDisabled = false
    }
  }

  private subscribeFormForValueChanges(formGroup: FormGroup) {
    const initialValue = formGroup?.value
    formGroup.valueChanges.subscribe(value => {
      this.hasFormChanged = Object.keys(initialValue).some(
        key => formGroup.value[key] != initialValue[key] && formGroup.dirty
      )
    })
  }

  determineNotifyState(commentToBeSaved: CommentAPIModel[]) {
    const hasCommented = commentToBeSaved?.length > 0
    const hasUploadedFiles = this.filesToBeUploaded !== null && this.filesToBeUploaded?.length > 0
    return ((this.hasFormChanged != null && this.hasFormChanged) || hasCommented || hasUploadedFiles) && this.notify
  }

  private setSelectedActivityType(activity: ActivityAPIModel) {
    this.selectTaskTypesSubscription$ = this.initializeService
      .getFilteredTaskTypes(GlobalConstants.MODULE.ACTIVITY_TRACKER, activity.taskCategory, activity.taskSubCategory)
      .subscribe((taskTypes: TaskTypeAPIModel[]) => {
        if (taskTypes) {
          const selectedTaskType = taskTypes.find(taskType => taskType.code === activity.taskCode)
          this.isEmployeeLinkedTask = selectedTaskType?.employeeLinkedTask
          this.isHeadcountRequired = selectedTaskType?.headcountRequired
          if (selectedTaskType?.employeeLinkedTask) {
            this.addOwnerDisplayIdToForm(this.activityDetailsForm)
            this.addEmpNameToForm(this.activityDetailsForm)
            this.addEmpStatusToForm(this.activityDetailsForm)
            this.addEmpCurrentPfNumberToForm(this.activityDetailsForm)
            this.addEmpUanNumberToForm(this.activityDetailsForm)
            this.f[this.OWNER_DISPLAY_ID_CONTROL_NAME].setValue(activity.ownerDisplayId)
            this.f[this.EMP_NAME_CONTROL_NAME].setValue(activity.empName)
            this.f[this.EMP_STATUS_CONTROL_NAME].setValue(activity.empStatus)
            this.f[this.EMP_CURRENT_PF_NO_CONTROL_NAME].setValue(activity.empCurrentPfNumber || '')
            this.f[this.EMP_UAN_NO_CONTROL_NAME].setValue(activity.empUanNumber || '')
          }
        }
      })
  }

  goToNewJoineeActivity() {
    const url = `/${GlobalConstants.ROUTING.FORM11_WORKFLOW}/${this.id}/${GlobalConstants.ROUTING.STATUS}/${this.activity.status}/${this.activity.ownerId}`
    this.cs.routeTo(url)
  }

  handleNewJoineeActvitySwitchValue(
    switchValue: boolean,
    switchType: 'PREVIOUS_EPF' | 'MEM_OF_EPS' | 'IW_STATUS' | 'PF_PORTAL_STATUS'
  ) {
    Swal.fire({
      title: `Are you sure you want to update the change?`,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: 'Ok',
      denyButtonText: 'Exit',
      color: '#000',
      confirmButtonColor: '#0452d7',
      denyButtonColor: '#444C66',
      reverseButtons: true
    }).then(result => {
      if (result.isConfirmed) {
        this.updateReferenceDetails(switchValue, switchType)
      } else {
        this.resetSwitchButtons(switchType, switchValue)
      }
    })
  }

  updateReferenceDetails(switchValue: boolean, switchType: string) {
    let input = null
    let activityStatus = 'INCOMPLETE_INCORRECT_SUBMISSION'
    if (switchType === 'PREVIOUS_EPF') {
      input = { epfDetails: { isExistingEPFMember: switchValue } }
    } else if (switchType === 'MEM_OF_EPS') {
      input = { epsDetails: { isEPSMember: switchValue } }
    } else if (switchType === 'IW_STATUS') {
      input = { passportDetails: { isInternationalWorker: switchValue } }
    } else if (switchType === 'PF_PORTAL_STATUS') {
      input = { employeeComplianceReferenceDetailsOrchModel: { pfPortalStatus: switchValue } }
      activityStatus = 'UPLOADED'
    }
    if (this.activity.status === 'COMPLETED') {
      activityStatus = 'COMPLETED'
    }
    this.form11Service
      .invokeUpdateReferenceDetailsAPI(input, null, null, null, this.id, this.activity.ownerId, activityStatus)
      .pipe(
        map(() => {
          this.cs.showSuccess('Saved Successfully')
        }),
        catchError(() => {
          this.cs.showError('Unable to save')
          this.resetSwitchButtons(switchType, switchValue)
          return of(null)
        })
      )
      .subscribe()
  }

  private resetSwitchButtons(switchType: string, switchValue: boolean) {
    const clonedActivity = cloneDeep(this.activity)
    this.activity = null
    this.cd.detectChanges()
    if (switchType === 'PREVIOUS_EPF') {
      this.activity = { ...clonedActivity, havePfAccount: !switchValue }
    } else if (switchType === 'MEM_OF_EPS') {
      this.activity = { ...clonedActivity, isMemberOfEmployeePensionScheme: !switchValue }
    } else if (switchType === 'IW_STATUS') {
      this.activity = { ...clonedActivity, isInternationalWorker: !switchValue }
    }
    this.activity = clonedActivity
  }

  loadLocationsForCompany(companyEntityId: string) {
    if (companyEntityId) {
      this.initializeService.getRelatedLocationMap().subscribe({
        next: relatedLocationMap => {
          if (relatedLocationMap?.size > 0) {
            const locationAPIModelArr: LocationAPIModel[] = relatedLocationMap.get(companyEntityId)
            if (locationAPIModelArr) {
              const locationMap = new Map<string, string>()
              locationAPIModelArr?.forEach(locationAPIModel => {
                locationMap.set(locationAPIModel.code, locationAPIModel.code)
              })
              this.locationOptions = locationMap
            }
          }
        }
      })
    }
  }

  ngOnDestroy(): void {
    this.activityService.dispatchDeleteActivityDetailsAction()
    this.selectTaskTypesSubscription$?.unsubscribe()
  }
}
